import { Badge, Card, CardContent, cn } from "@tellescope/ui";
import { formatDate } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { FileText, CheckCircle2, Clock, Edit3, ChevronRight } from "lucide-react";
import type { FormResponse } from "@tellescope/types-models";

const getDisplayTitle = (formTitle: string, titleOverrides?: Record<string, string>): string => {
  return titleOverrides?.[formTitle] ?? formTitle;
};

export interface FormResponseCardProps {
  formResponse: FormResponse;
  titleOverrides?: Record<string, string>;
  onClick: () => void;
}

export const FormResponseCard = ({ formResponse, titleOverrides, onClick }: FormResponseCardProps) => {
  const { t } = useTranslation("forms");
  const isSubmitted = !!formResponse.submittedAt;
  const hasDraft = !!formResponse.draftSavedAt;

  return (
    <Card className="overflow-hidden cursor-pointer hover:bg-accent/50 transition-colors" onClick={onClick}>
      <CardContent className="px-4">
        <div className="flex gap-4">
          <div className="flex-shrink-0">
            <div
              className={cn(
                "w-14 h-14 rounded-lg flex flex-col items-center justify-center",
                isSubmitted ? "bg-green-100 dark:bg-green-900/30" : "bg-amber-100 dark:bg-amber-900/30"
              )}
            >
              {isSubmitted ? (
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              ) : (
                <Edit3 className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-sm truncate">
                {getDisplayTitle(formResponse.formTitle, titleOverrides)}
              </h4>
              <Badge variant={isSubmitted ? "secondary" : "outline"} className="flex-shrink-0 text-xs">
                {isSubmitted ? t("Submitted") : hasDraft ? t("Draft") : t("Pending")}
              </Badge>
            </div>

            {isSubmitted && formResponse.submittedAt && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Clock className="h-3 w-3" />
                <span>{t("Submitted {{timestamp}}", { timestamp: formatDate(formResponse.submittedAt) })}</span>
              </div>
            )}

            {!isSubmitted && hasDraft && formResponse.draftSavedAt && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Clock className="h-3 w-3" />
                <span>{t("Draft saved {{timestamp}}", { timestamp: formatDate(formResponse.draftSavedAt) })}</span>
              </div>
            )}

            {!isSubmitted && !hasDraft && (
              <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1">
                <FileText className="h-3 w-3" />
                <span>{t("Needs completion")}</span>
              </div>
            )}

            {formResponse.scores && formResponse.scores.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formResponse.scores.slice(0, 2).map((score, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {score.title}: {score.value}
                  </Badge>
                ))}
                {formResponse.scores.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{formResponse.scores.length - 2}
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="flex-shrink-0 self-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
